import { InputNumberProps } from 'antd';
import { StyledNumberInput } from './styles';
import { useDebouncedField } from 'hooks/useDebouncedField';

type Props = {
    name: string;
    integerOnly?: boolean;
    precision?: number;
    hideControls?: boolean;
    debounceMs?: number;
    validateOnDebounce?: boolean;
    onDebouncedChange?: (value: number | null) => void;
} & Omit<InputNumberProps, 'value' | 'id' | 'status'>;

const FormNumberInput = ({
    name,
    integerOnly = false,
    precision,
    hideControls,
    step,
    debounceMs = 250,
    validateOnDebounce,
    parser,
    onChange,
    onDebouncedChange,
    ...rest
}: Props) => {
    const { meta, helpers, local, handleChange } = useDebouncedField<number>({
        name,
        debounceMs,
        validateOnDebounce,
        onDebouncedChange,
    });

    const effectiveParser =
        parser ??
        ((v?: string) => {
            if (!v) return '';
            return integerOnly
                ? v.replace(/[,.]\d*/g, '') // strip anything after . or ,
                : v.replace(',', '.'); // allow decimals, normalize comma
        });

    const toNumber = (val: unknown) => {
        if (val === null || val === undefined || val === '') return null;
        const num = Number(val);
        if (!Number.isFinite(num)) return null;
        return integerOnly ? Math.trunc(num) : num;
    };

    return (
        <>
            <StyledNumberInput
                $hideControls={hideControls}
                id={name}
                value={local}
                step={step ?? (integerOnly ? 1 : undefined)}
                precision={integerOnly ? 0 : precision}
                parser={effectiveParser}
                onChange={(val) => {
                    const n = toNumber(val);
                    handleChange(n);
                    onChange?.(n);
                }}
                onBlur={() => helpers.setTouched(true)}
                status={meta?.error && !!meta?.touched ? 'error' : ''}
                {...rest}
            />

            {meta.touched && meta.error ? <div style={{ color: 'var(--error-normal)' }}>{meta.error}</div> : null}
        </>
    );
};

export default FormNumberInput;
